import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from "@floating-ui/react";
import React, { useRef, useState } from "react";

import Icon from "components/general/Icon";
import iconMore from "images/ic_more.png";
import styled from "styled-components";
import theme from "styles/theme";

const MoreButtonContainer = styled.button`
  &:hover {
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 50%;
  }
`;

const PopoverContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${theme.spacing.unit * 1}px;
  pointer-events: auto;
  background-color: ${theme.colors.white};

  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.3),
    0px 1px 3px 1px rgba(0, 0, 0, 0.15);

  border-radius: 8px;
  padding: 4px;
  width: 211px;
  box-sizing: border-box;
  overflow: hidden;

  margin: 0;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: ${theme.spacing.unit * 1}px;
  padding: 13.5px ${theme.spacing.unit * 2}px;
  box-sizing: border-box;
  cursor: pointer;
  border-radius: 8px;

  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  color: ${(props) => props.color};

  &:hover {
    background-color: ${theme.colors.gray1};
  }
`;

function PopoverMenu({
  className,
  brandingData,
  onGetEmbedLinkClick,
  onGoToFullClipClick,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-end",
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(8)],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "menu" });

  const listRef = useRef([]);
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [click, dismiss, role, listNavigation]
  );

  const handleGetEmbedLinkClick = (e) => {
    e.stopPropagation();

    onGetEmbedLinkClick();

    setIsOpen(false);
  };

  const handleGoToFullClipClick = (e) => {
    e.stopPropagation();

    onGoToFullClipClick();

    setIsOpen(false);
  };

  const items = [
    { title: "Get embed link", onClick: handleGetEmbedLinkClick },
    {
      title: "Go to full clip with transcript",
      onClick: handleGoToFullClipClick,
    },
  ];

  return (
    <>
      <MoreButtonContainer
        className={className}
        ref={refs.setReference}
        {...getReferenceProps({
          onClick(e) {
            e.stopPropagation();
          },
        })}
        aria-label={"More options"}
      >
        <Icon src={iconMore} iconColor={brandingData.primaryTextColor} />
      </MoreButtonContainer>
      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <PopoverContainer
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              {items.map((item, index) => (
                <MenuItem
                  key={item.title}
                  role="menuitem"
                  tabIndex={activeIndex === index ? 0 : -1}
                  ref={(node) => {
                    listRef.current[index] = node;
                  }}
                  {...getItemProps({
                    onClick(e) {
                      item.onClick(e);
                    },
                    onKeyDown(e) {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        item.onClick(e);
                      }
                    },
                  })}
                  color={brandingData.primaryTextColor}
                >
                  {item.title}
                </MenuItem>
              ))}
            </PopoverContainer>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}

export default PopoverMenu;
